import { useState, useEffect, type FC } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';
import type {
  ProjectMemberCardId,
  ProjectMemberCalendarEvent,
  ProjectMemberJointArticle,
  ProjectMemberWorkspaceSummary,
} from './types';
import {
  addProjectMemberWorkItemDecision,
  addProjectMemberWorkItemResult,
  createProjectMemberArticle,
  createProjectMemberCalendarEvent,
  deleteProjectMemberCalendarEvent,
  getProjectMemberAISuggestion,
  getProjectMemberSummary,
  updateProjectMemberArticle,
  updateProjectMemberWorkItem,
} from './api';
import { ProjectMemberCabinetGrid } from './components/ProjectMemberCabinetGrid';
import { ProjectMemberRequestsOverlay } from './components/ProjectMemberRequestsOverlay';
import { ProjectMemberCalendarOverlay } from './components/ProjectMemberCalendarOverlay';
import { ProjectMemberArticlesOverlay } from './components/ProjectMemberArticlesOverlay';
import { ProjectMemberAIModal } from './components/ProjectMemberAIModal';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';

const CARD_PATHS: Record<ProjectMemberCardId, string> = {
  MY_REQUESTS: 'requests',
  PROJECT_CALENDAR: 'calendar',
  RESEARCH_TOOLS: 'tools',
  ACADEMIC_CONNECTIONS: 'connections',
  B5_FILES: 'files',
  B5_ARTICLES: 'articles',
};

export const ProjectMemberPage: FC = () => {
  const { user } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ProjectMemberWorkspaceSummary | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState<boolean>(false);
  const backToCabinet = () => navigate(routes.projectMember);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const data = await getProjectMemberSummary();
      setSummary(data);
    } catch {
      setSummary(null);
      setLoadError(true);
      notify('Не удалось загрузить кабинет участника проекта', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const handleOpenOverlay = (id: ProjectMemberCardId) => {
    const segment = CARD_PATHS[id];
    if (segment) navigate(segment);
  };

  const handleUpdateStatus = async (itemId: string, status: string) => {
    try {
      const updated = await updateProjectMemberWorkItem(itemId, { status });
      if (summary) {
        setSummary({
          ...summary,
          work_items: summary.work_items.map((w) => (w.id === itemId ? updated : w)),
        });
      }
    } catch {
      await loadData();
    }
  };

  const handleAddDecision = async (itemId: string, statusAfter: string, decisionText: string) => {
    try {
      const decision = await addProjectMemberWorkItemDecision(itemId, {
        status_after: statusAfter,
        decision_text: decisionText,
      });
      if (summary) {
        setSummary({
          ...summary,
          work_items: summary.work_items.map((w) =>
            w.id === itemId ? { ...w, status: statusAfter, decisions: [decision, ...w.decisions] } : w
          ),
        });
      }
    } catch {
      await loadData();
    }
  };

  const handleAddResult = async (itemId: string, title: string, fileName?: string) => {
    try {
      const result = await addProjectMemberWorkItemResult(itemId, {
        title,
        file_name: fileName,
        file_size_bytes: 1048576,
      });
      if (summary) {
        setSummary({
          ...summary,
          work_items: summary.work_items.map((w) =>
            w.id === itemId ? { ...w, results: [result, ...w.results] } : w
          ),
        });
      }
    } catch {
      await loadData();
    }
  };

  const handleCreateCalendarEvent = async (payload: Partial<ProjectMemberCalendarEvent>) => {
    try {
      const newEvt = await createProjectMemberCalendarEvent(payload);
      if (summary) {
        setSummary({
          ...summary,
          calendar_events: [newEvt, ...summary.calendar_events],
        });
      }
    } catch {
      await loadData();
    }
  };

  const handleDeleteCalendarEvent = async (eventId: string) => {
    try {
      await deleteProjectMemberCalendarEvent(eventId);
      if (summary) {
        setSummary({
          ...summary,
          calendar_events: summary.calendar_events.filter((e) => e.id !== eventId),
        });
      }
    } catch {
      await loadData();
    }
  };

  const handleCreateArticle = async (payload: Partial<ProjectMemberJointArticle>) => {
    try {
      const art = await createProjectMemberArticle(payload);
      if (summary) {
        setSummary({
          ...summary,
          articles: [art, ...summary.articles],
        });
      }
    } catch {
      await loadData();
    }
  };

  const handleUpdateArticle = async (articleId: string, payload: Partial<ProjectMemberJointArticle>) => {
    try {
      const updated = await updateProjectMemberArticle(articleId, payload);
      if (summary) {
        setSummary({
          ...summary,
          articles: summary.articles.map((a) => (a.id === articleId ? updated : a)),
        });
      }
    } catch {
      await loadData();
    }
  };

  const handleAskAI = async (prompt: string) => {
    return getProjectMemberAISuggestion({ prompt });
  };

  const profile = summary?.profile || null;
  const stats = summary?.stats || null;

  if (isLoading && !summary) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-950 space-y-4">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-400 rounded-full animate-spin" />
        <span className="text-sm text-slate-400 font-medium">Загрузка кабинета участника проекта...</span>
      </div>
    );
  }

  if (!summary || loadError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-950 space-y-4">
        <span className="text-sm text-slate-400 font-medium">Не удалось загрузить данные кабинета</span>
        <button
          type="button"
          onClick={() => void loadData()}
          className="rounded-xl bg-emerald-500 px-4 py-2 text-xs font-extrabold text-slate-950 hover:bg-emerald-400 transition"
        >
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 selection:bg-emerald-500 selection:text-slate-950 role-cabinet-page">
      <Routes>
      <Route
        index
        element={
      <CabinetWorkspaceFrame
        links={{
          notebook: `${routes.projectMember}/files`,
          calendar: `${routes.projectMember}/calendar`,
          meetings: `${routes.projectMember}/calendar`,
          connections: `${routes.projectMember}/connections`,
          alliances: `${routes.projectMember}/articles`,
          qa: routes.portalSection('instructions'),
          support: routes.portalSection('contacts'),
        }}
      >
      <div className="w-full px-0 py-6 space-y-6">
        <div className="role-hero" style={{ ['--role-accent' as string]: '#49ddb7' }}>
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-600 to-cyan-500 font-extrabold text-lg text-white">
              {profile?.full_name ? profile.full_name.slice(0, 2).toUpperCase() : 'УП'}
            </div>
            <div className="min-w-0">
              <small>
                {profile?.user_code || '—'} · Участник проекта
              </small>
              <h2 className="truncate">
                {profile?.full_name || (user ? `${user.firstName} ${user.lastName}` : 'Участник проекта')}
              </h2>
              <p>
                {profile?.position || 'Участник проекта'}
                {profile?.organization ? ` · ${profile.organization}` : ''}
              </p>
            </div>
          </div>
          <div className="role-hero-actions">
            <button type="button" className="role-btn" onClick={() => setIsAIModalOpen(true)}>
              Super AI
            </button>
          </div>
        </div>

        <div className="role-statgrid mt-5">
          <button
            type="button"
            className="role-stat"
            style={{ ['--a' as string]: '#49ddb7' }}
            onClick={() => handleOpenOverlay('MY_REQUESTS')}
          >
            <strong>{stats?.total_requests ?? 0}</strong>
            <span>Всего заявок</span>
          </button>
          <button
            type="button"
            className="role-stat"
            style={{ ['--a' as string]: '#41d2ff' }}
            onClick={() => handleOpenOverlay('MY_REQUESTS')}
          >
            <strong>{stats?.in_work_requests ?? 0}</strong>
            <span>В работе</span>
          </button>
          <button
            type="button"
            className="role-stat"
            style={{ ['--a' as string]: '#e2ad3a' }}
            onClick={() => handleOpenOverlay('PROJECT_CALENDAR')}
          >
            <strong>{stats?.deadlines_count ?? 0}</strong>
            <span>Дедлайны</span>
          </button>
          <button
            type="button"
            className="role-stat"
            style={{ ['--a' as string]: '#9e80ff' }}
            onClick={() => handleOpenOverlay('ACADEMIC_CONNECTIONS')}
          >
            <strong>{stats?.unread_messages_count ?? 0}</strong>
            <span>Сообщения</span>
          </button>
        </div>

        <div className="owner-hub-top-actions mt-5">
          <button type="button" className="owner-hub-ai-action" onClick={() => setIsAIModalOpen(true)}>
            <span className="spark" aria-hidden="true">
              ✦
            </span>
            <span>
              <strong>Super AI</strong>
              <small>Помощник участника проекта по заявкам и материалам</small>
            </span>
            <b>Открыть →</b>
          </button>
        </div>

        <ProjectMemberCabinetGrid
          onCardClick={handleOpenOverlay}
        />

                <div className="mt-8 space-y-6">
            <div className="rounded-2xl border border-slate-800/80 bg-slate-900/60 p-5 backdrop-blur-md">
              <div className="flex items-center justify-between border-b border-slate-800/80 pb-3 mb-4">
                <div>
                  <span className="text-[10px] font-mono font-bold tracking-wider text-slate-500 uppercase">
                    РАБОЧАЯ ОЧЕРЕДЬ
                  </span>
                  <h3 className="text-base font-bold text-white mt-0.5">Заявки в работе</h3>
                </div>
                <button
                  onClick={() => handleOpenOverlay('MY_REQUESTS')}
                  className="text-xs text-emerald-400 hover:underline font-semibold"
                >
                  Открыть все заявки →
                </button>
              </div>

              <div className="space-y-2.5">
                {(summary?.active_queue || []).map((q) => (
                  <div
                    key={q.code}
                    onClick={() => handleOpenOverlay('MY_REQUESTS')}
                    className="flex items-center justify-between rounded-xl border border-slate-800/80 bg-slate-950/50 p-3.5 hover:border-slate-700 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                        {q.code}
                      </span>
                      <span className="text-xs font-semibold text-white truncate max-w-xs sm:max-w-md">
                        {q.title}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-slate-400 font-mono text-[11px]">{q.due}</span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                        {q.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <ScientificGpsBlock openTo={`${routes.projectMember}/articles`} />
        </div>

        <section className="mt-8 rounded-2xl border border-slate-800/80 bg-slate-900/60 p-6 backdrop-blur-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-white">Новости науки и возможности</h3>
            <span className="text-xs text-slate-500">Все новости науки →</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
              <span className="text-[10px] font-mono text-emerald-400 font-bold uppercase">
                Исследовательская инфраструктура
              </span>
              <h4 className="text-xs font-bold text-white mt-2 leading-snug">
                Новые возможности для работы с научными данными и открытыми публикациями
              </h4>
              <p className="text-[11px] text-slate-400 mt-2 line-clamp-2">
                Доступны новые коннекторы к базам данных OpenAlex и Crossref для участников проектов.
              </p>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
              <span className="text-[10px] font-mono text-cyan-400 font-bold uppercase">
                Публикации
              </span>
              <h4 className="text-xs font-bold text-white mt-2 leading-snug">
                Практический чек-лист проверки рукописи перед подачей в международный журнал
              </h4>
              <p className="text-[11px] text-slate-400 mt-2 line-clamp-2">
                Рекомендации рецензентов и стандарты оформления библиографических списков.
              </p>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
              <span className="text-[10px] font-mono text-amber-400 font-bold uppercase">
                Digital Humanities
              </span>
              <h4 className="text-xs font-bold text-white mt-2 leading-snug">
                Корпусные и NLP-инструменты всё чаще объединяются в междисциплинарных исследованиях
              </h4>
              <p className="text-[11px] text-slate-400 mt-2 line-clamp-2">
                Опыт применения методов анализа семантики на материале казахского фольклора.
              </p>
            </div>
          </div>
        </section>
      </div>
      </CabinetWorkspaceFrame>
        }
      />

      <Route
        path="requests"
        element={
          <ProjectMemberRequestsOverlay
            isOpen
            profile={profile}
            workItems={summary?.work_items || []}
            onClose={backToCabinet}
            onUpdateStatus={handleUpdateStatus}
            onAddDecision={handleAddDecision}
            onAddResult={handleAddResult}
          />
        }
      />
      <Route
        path="calendar"
        element={
          <ProjectMemberCalendarOverlay
            isOpen
            profile={profile}
            events={summary?.calendar_events || []}
            onClose={backToCabinet}
            onCreateEvent={handleCreateCalendarEvent}
            onDeleteEvent={handleDeleteCalendarEvent}
          />
        }
      />
      <Route
        path="connections"
        element={
          <RoleAcademicConnectionsOverlay
            isOpen
            onClose={backToCabinet}
            roleContext="PROJECT_MEMBER"
            roleTitle="Участник проекта"
          />
        }
      />
      <Route
        path="tools/*"
        element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path="files/*"
        element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path="articles"
        element={
          <ProjectMemberArticlesOverlay
            isOpen
            profile={profile}
            articles={summary?.articles || []}
            onClose={backToCabinet}
            onCreateArticle={handleCreateArticle}
            onUpdateArticle={handleUpdateArticle}
          />
        }
      />
      </Routes>

      <ProjectMemberAIModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onAskAI={handleAskAI}
      />
    </div>
  );
};
export default ProjectMemberPage;
